import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  TrendingUp as StrategyIcon,
  Balance as LeverageIcon,
  Handshake as DealIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Balance as NegotiationIcon,
} from '@mui/icons-material';
import { NegotiationResult } from '../../../types/property';

interface NegotiationTabProps {
  data: NegotiationResult;
  loading?: boolean;
  error?: string;
}

const NegotiationTab: React.FC<NegotiationTabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading negotiation analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No negotiation analysis available
      </Alert>
    );
  }

  const { buyerPsychology, negotiationStrategy, marketLeverage, dealOptimization, communicationStrategy, recommendations, riskAssessment } = data;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getRiskColor = (risk: number) => {
    if (risk < 0.3) return 'success';
    if (risk < 0.6) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Negotiation Analysis
        </Typography>
        <Button variant="contained" startIcon={<NegotiationIcon />}>
          View Full Report
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        {/* Buyer Psychology */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PsychologyIcon color="primary" />
              Buyer Psychology
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Buyer Type</Typography>
              <Chip label={buyerPsychology.buyerType} color="primary" size="small" />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Motivation Level</Typography>
              <Chip 
                label={buyerPsychology.motivationLevel} 
                color={buyerPsychology.motivationLevel === 'high' ? 'error' : buyerPsychology.motivationLevel === 'medium' ? 'warning' : 'success'} 
                size="small" 
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Negotiation Style</Typography>
              <Typography variant="body1">{buyerPsychology.negotiationStyle}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Risk Tolerance</Typography>
              <Typography variant="body1">{buyerPsychology.riskTolerance}</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Decision Drivers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {buyerPsychology.decisionDrivers.map((driver, index) => (
                <Chip key={index} label={driver} size="small" variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Market Leverage */}
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LeverageIcon color="primary" />
              Market Leverage
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Market Conditions</Typography>
              <Chip 
                label={marketLeverage.marketConditions} 
                color={marketLeverage.marketConditions === 'seller-market' ? 'error' : marketLeverage.marketConditions === 'balanced' ? 'warning' : 'success'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Competition Level</Typography>
              <Chip 
                label={marketLeverage.competitionLevel} 
                color={marketLeverage.competitionLevel === 'high' ? 'error' : marketLeverage.competitionLevel === 'medium' ? 'warning' : 'success'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Time Advantage</Typography>
              <Typography variant="body1">{marketLeverage.timeAdvantage}</Typography>
            </Box>

            {marketLeverage.sellerAdvantages.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Seller Advantages
                </Typography>
                <List dense>
                  {marketLeverage.sellerAdvantages.slice(0, 3).map((advantage, index) => (
                    <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={advantage} 
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Negotiation Strategy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StrategyIcon color="primary" />
            Negotiation Strategy
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'primary.contrastText', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  {formatPrice(negotiationStrategy.initialPosition.price)}
                </Typography>
                <Typography variant="subtitle2">
                  Initial Position
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Initial Terms
              </Typography>
              <List dense>
                {negotiationStrategy.initialPosition.terms.slice(0, 3).map((term, index) => (
                  <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckIcon fontSize="small" color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={term} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.contrastText', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  {formatPrice(negotiationStrategy.targetPosition.price)}
                </Typography>
                <Typography variant="subtitle2">
                  Target Position
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Must Haves
              </Typography>
              <List dense>
                {negotiationStrategy.targetPosition.mustHaves.slice(0, 3).map((item, index) => (
                  <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <CheckIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2, color: 'warning.contrastText', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                  {formatPrice(negotiationStrategy.walkAwayPosition.price)}
                </Typography>
                <Typography variant="subtitle2">
                  Walk Away Point
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Deal Breakers
              </Typography>
              <List dense>
                {negotiationStrategy.walkAwayPosition.dealBreakers.slice(0, 3).map((breaker, index) => (
                  <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <WarningIcon fontSize="small" color="warning" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={breaker} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Deal Optimization */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DealIcon color="primary" />
            Deal Optimization
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Optimal Terms
            </Typography>
            <Typography variant="h6" color="primary">
              {formatPrice(dealOptimization.optimalTerms.price)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Closing: {dealOptimization.optimalTerms.closingDate}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Value Enhancements
          </Typography>
          <List dense>
            {dealOptimization.valueEnhancements.immediate.slice(0, 3).map((enhancement, index) => (
              <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary={enhancement} 
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Risk Assessment */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="primary" />
            Risk Assessment
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="h6" color={getRiskColor(riskAssessment.dealFailure)}>
                  {Math.round(riskAssessment.dealFailure * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Deal Failure Risk
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="h6" color={getRiskColor(riskAssessment.valueLoss)}>
                  {Math.round(riskAssessment.valueLoss * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Value Loss Risk
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="h6" color={getRiskColor(riskAssessment.timelineRisk)}>
                  {Math.round(riskAssessment.timelineRisk * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Timeline Risk
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="h6" color={getRiskColor(riskAssessment.relationshipRisk)}>
                  {Math.round(riskAssessment.relationshipRisk * 100)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Relationship Risk
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon color="primary" />
            Action Plan
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'error.main' }}>
                Immediate Actions
              </Typography>
              <List dense>
                {recommendations.immediate.map((rec, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'error.main', fontSize: '0.75rem' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={rec} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.main' }}>
                Preparation
              </Typography>
              <List dense>
                {recommendations.preparation.map((rec, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'warning.main', fontSize: '0.75rem' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={rec} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'info.main' }}>
                Execution
              </Typography>
              <List dense>
                {recommendations.execution.map((rec, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'info.main', fontSize: '0.75rem' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={rec} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
                Follow-up
              </Typography>
              <List dense>
                {recommendations.followUp.map((rec, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main', fontSize: '0.75rem' }}>
                        {index + 1}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText 
                      primary={rec} 
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NegotiationTab;